import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { InsuranceCoverage } from '../../../models/parcelDeliveryMiles';
import { useStore } from '../../../stores/store';
import { transformToDecimal } from '../../../utils/forms/forms.utils';
import {
  InsuredValueButtonContainer,
  InsuredValueInputControl,
  InsuredValueCurrencyCode,
  InsuredValueField,
  InsuredValueInput,
  InsuredValueInputContainer,
  InsuredValueInputLabel,
  InsuredValueMessage,
} from './insurance-fee.styles';
import InputMessage from '../../../common/input-message/input-message.component';
import { getFormattedPrice } from '../../../utils/price/get-formatted-price';
import Button from '../../../common/button/button.component';

interface Props {
  currentCoverage: InsuranceCoverage;
  isFullCoverageDisabled: boolean;
  currencySymbol: string;
}

const ManualInsuredValueInput = ({
  currentCoverage,
  isFullCoverageDisabled,
  currencySymbol,
}: Props) => {
  const { t } = useTranslation();
  const {
    parcelCreationStore: {
      shipment,
      insuranceLimits,
      modifyShipmentInsurance,
      isLoadingShipmentApi,
    },
  } = useStore();

  const [insuredValueInput, setInsuredValueInput] = useState('');
  const [insuranceValue, setInsuranceValue] = useState<number>();
  const [error, setError] = useState<string>();

  const totalDeclaredValue = shipment?.total_declared_value ?? 0;
  const maxInsurance = insuranceLimits?.max_insurance ?? 0;
  const minAllowedInsurance =
    totalDeclaredValue > (insuranceLimits?.min_insurance ?? 0)
      ? totalDeclaredValue
      : insuranceLimits?.min_insurance ?? 0;
  const isBasicCoverage = currentCoverage === 'minimal_coverage';
  const isInputDisabled = isBasicCoverage || isFullCoverageDisabled;
  const isSubmitDisabled =
    isInputDisabled ||
    isLoadingShipmentApi ||
    insuranceValue === undefined ||
    !!error ||
    insuranceValue === shipment?.insurance;

  const getFormattedInsuranceLimit = useCallback(
    (value: number) => getFormattedPrice(value, currencySymbol),
    [currencySymbol]
  );

  // Sync with 'shipment'.
  // For basic mode - take insured value from total declared value or from limits.min_insurance;
  // For full mode -  take insured value from insurance.
  useEffect(() => {
    if (isBasicCoverage) {
      setInsuredValueInput(minAllowedInsurance.toString());
      return;
    }

    if (!shipment?.insurance) return;
    setInsuredValueInput(shipment.insurance.toString());
  }, [minAllowedInsurance, shipment?.insurance, isBasicCoverage]);

  useEffect(() => {
    const parsedInsuredValue = Number.parseFloat(insuredValueInput);
    setInsuranceValue(
      Number.isNaN(parsedInsuredValue) ? undefined : parsedInsuredValue
    );
  }, [insuredValueInput]);

  useEffect(() => {
    if (isBasicCoverage || insuranceValue === undefined) return;

    if (insuranceValue > maxInsurance) {
      setError(
        t('max_insurance_amount', {
          maxInsuranceAmount: getFormattedInsuranceLimit(maxInsurance),
        })
      );
      return;
    }

    if (insuranceValue < minAllowedInsurance) {
      setError(
        t('min_insurance_amount', {
          minInsuranceAmount: getFormattedInsuranceLimit(minAllowedInsurance),
        })
      );
      return;
    }

    setError(undefined);
  }, [
    insuranceValue,
    maxInsurance,
    minAllowedInsurance,
    isBasicCoverage,
    t,
    getFormattedInsuranceLimit,
  ]);

  const handleInsuredValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = transformToDecimal(event.target.value, 2);
    setInsuredValueInput(value);
  };

  const handleSubmitInsuredValue = useCallback(async () => {
    if (isSubmitDisabled || insuranceValue === undefined) return;

    await modifyShipmentInsurance(insuranceValue);
  }, [insuranceValue, isSubmitDisabled, modifyShipmentInsurance]);

  if (!insuranceLimits) return null;

  return (
    <InsuredValueInputContainer
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}>
      <InsuredValueInputControl>
        <InsuredValueInputLabel>{t('insured_value')}</InsuredValueInputLabel>
        <InsuredValueField $error={!!error}>
          <InsuredValueInput
            name='insured-value'
            value={insuredValueInput}
            inputMode='decimal'
            disabled={isInputDisabled}
            onChange={handleInsuredValueChange}
          />
          <InsuredValueCurrencyCode>{currencySymbol}</InsuredValueCurrencyCode>
        </InsuredValueField>
        {error && (
          <InsuredValueMessage>
            <InputMessage
              type='error'
              noMargin>
              {error}
            </InputMessage>
          </InsuredValueMessage>
        )}
        <InsuredValueButtonContainer>
          <Button
            appearance='primary'
            size='medium'
            fullWidth
            isLoading={isLoadingShipmentApi}
            disabled={isSubmitDisabled}
            onClick={handleSubmitInsuredValue}>
            {t('apply')}
          </Button>
        </InsuredValueButtonContainer>
      </InsuredValueInputControl>
    </InsuredValueInputContainer>
  );
};

export default observer(ManualInsuredValueInput);
